import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Lock, Mail, ArrowUpRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import { apiErrorMessage } from '../../lib/apiError';

export default function PartnerLogin() {
  const nav = useNavigate();
  const { partner, login, loading } = usePartnerAuth();
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && partner) nav('/partner', { replace: true });
  }, [partner, loading, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !pw) return toast.error('Enter your email and password');
    setBusy(true);
    try {
      await login(email, pw);
      toast.success('Signed in');
      nav('/partner', { replace: true });
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F1E8] flex items-center justify-center p-6">
      <div className="w-full max-w-[420px]">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-full bg-[#0B3B2E] flex items-center justify-center">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M4 20 L12 4 L20 20" stroke="#C8944B" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span className="font-serif text-[22px] font-semibold text-[#0B3B2E]">
            Vector<span className="text-[#C8944B]">FX</span>
          </span>
        </Link>

        <div className="bg-white rounded-2xl border border-[#0B3B2E]/10 shadow-xl p-8">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#0B3B2E]/70 font-medium">Partner portal</p>
          <h1 className="mt-2 font-serif text-[30px] leading-tight text-[#0B3B2E]">Welcome back</h1>
          <p className="mt-2 text-[13px] text-[#0E1F1A]/60">Sign in to submit referrals and track their status.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="em" className="text-[13px] text-[#0E1F1A]/70">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0B3B2E]/50" />
                <Input id="em" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="pl-9 h-11 bg-[#F5F1E8] border-[#0B3B2E]/20 focus-visible:ring-[#0B3B2E]/30 focus-visible:border-[#0B3B2E]"
                  autoComplete="email" placeholder="you@company.com" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw" className="text-[13px] text-[#0E1F1A]/70">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0B3B2E]/50" />
                <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                  className="pl-9 h-11 bg-[#F5F1E8] border-[#0B3B2E]/20 focus-visible:ring-[#0B3B2E]/30 focus-visible:border-[#0B3B2E]"
                  autoComplete="current-password" placeholder="••••••••" />
              </div>
            </div>
            <Button type="submit" disabled={busy}
              className="w-full rounded-full h-11 bg-[#0B3B2E] text-[#F5F1E8] hover:bg-[#12503F] font-medium disabled:opacity-70">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in</>
                : <>Sign in <ArrowUpRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </form>

          <p className="mt-6 text-center text-[13px] text-[#0E1F1A]/60">
            New here? <Link to="/partner/signup" className="underline text-[#0B3B2E] font-medium">Become a partner</Link>
          </p>
          <p className="mt-2 text-center text-[12px] text-[#0E1F1A]/50">
            Staff? <Link to="/crm/login" className="underline text-[#0B3B2E]">CRM sign in</Link> · <Link to="/" className="underline text-[#0B3B2E]">Back to site</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
